import { Dialog, DialogClose, DialogContent, DialogHeader, DialogTitle } from '@leadsnavi/ui-kit';
import { Minimize2, RotateCcw } from 'lucide-react';
import { Button } from '@leadsnavi/ui-kit';
import { useMemoizedFn, useRequest } from 'ahooks';
import cn from 'classnames';
import React from 'react';
import InsertArrowIcon from '@/assets/icons/insert-arrow.svg?react';
import { EmailContentViewerV2 } from '@/common/base/email-content-viewerV2';
import { emailExpandPost } from '@/services/swagger/easourcing/SessionService';
import type { IApiSessionEmailExpandReq } from '@/services/swagger/easourcing/models';
import { useDialogVisible } from '@/hooks';

export interface EmailPreviewDialogProps {
  /** 是否打开 */
  open: boolean;
  /** email-expand 接口入参 */
  emailExpandParams: Required<IApiSessionEmailExpandReq>;
  /** 打开/关闭回调 */
  onOpenChange?: (open: boolean) => void;
  /** 重试回调 */
  onRetry?: (emailExpandParams: Required<IApiSessionEmailExpandReq>) => void;
  /** 插入回调 */
  onInsert?: (emailExpandParams: Required<IApiSessionEmailExpandReq>) => void;
  /** 最小化回调 */
  onMinimize?: (emailExpandParams: Required<IApiSessionEmailExpandReq>) => void;
  /** 自定义类名 */
  disableButton?: boolean;
  className?: string;
}
/**
 * 邮件预览 Dialog 组件
 * 展示邮件的完整预览内容，包含主题、文本、图片和操作按钮
 */
const PureEmailPreviewDialog: React.FC<EmailPreviewDialogProps> = props => {
  const {
    open,
    onOpenChange,
    onRetry,
    onInsert,
    onMinimize,
    disableButton = false,
    className,
    emailExpandParams,
  } = props;

  const [retryLoading, setRetryLoading] = React.useState(false);
  const [insertLoading, setInsertLoading] = React.useState(false);

  /** 调用 email-expand 接口 */
  const {
    run: runEmailExpand,
    data: emailExpandData,
    loading,
  } = useRequest(emailExpandPost, {
    manual: true,
  });

  const handleRetry = useMemoizedFn(async () => {
    setRetryLoading(true);
    try {
      await onRetry?.(emailExpandParams);
    } finally {
      setRetryLoading(false);
    }
  });

  const handleInsert = useMemoizedFn(async () => {
    setInsertLoading(true);
    try {
      await onInsert?.(emailExpandParams);
    } finally {
      setInsertLoading(false);
    }
  });

  const handleMinimize = useMemoizedFn(() => {
    onMinimize?.(emailExpandParams);
  });

  /** Dialog 打开时触发接口请求 */
  React.useEffect(() => {
    if (
      open &&
      emailExpandParams &&
      emailExpandParams.sessionId &&
      emailExpandParams.messageId &&
      emailExpandParams.subMessageId
    ) {
      runEmailExpand(emailExpandParams);
    }
  }, [open, emailExpandParams, runEmailExpand]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className={cn(' h-[80vh] max-h-[765px]! p-0 bg-[#F5F5F5] shadow-lg overflow-hidden flex flex-col', className)}
        style={{
          width: '80vw',
          maxWidth: '1280px',
        }}
      >
        {/* 头部 */}
        <DialogHeader className="bg-white px-4 py-4 border-b border-transparent">
          <div className="flex flex-row justify-between items-center w-full">
            <DialogTitle className="text-[#171717] text-xl font-semibold m-0">Preview mode</DialogTitle>
            <DialogClose>
              <button onClick={handleMinimize} className="w-4 h-4 flex items-center justify-center cursor-pointer">
                <Minimize2 className="w-4 h-4 text-[#262626]" />
              </button>
            </DialogClose>
          </div>
        </DialogHeader>

        {/* 内容区域 */}
        <div className="flex-1 overflow-auto px-4">
          {/* 预览卡片 */}
          <div className="bg-white rounded-[8px] p-4 flex flex-col gap-2.5 h-full">
            {/* 主题行 */}
            <div className="flex flex-row items-center gap-4">
              <span className="text-[#09090B] text-base font-medium">{emailExpandData?.data?.emailSubject || ''}</span>
            </div>
            <EmailContentViewerV2 content={emailExpandData?.data?.emailContent || ''} />
          </div>
        </div>
        {/* 底部操作区 */}

        {disableButton ? null : (
          <div className="bg-white border-t border-[#E5E5E5] px-4 py-4 flex flex-row justify-end items-center gap-3">
            <Button
              disabled={loading}
              loading={retryLoading}
              variant="outline"
              size="sm"
              onClick={handleRetry}
              className="flex items-center gap-1.5"
            >
              <RotateCcw className="w-4 h-4" />
              Retry
            </Button>
            <Button
              disabled={loading}
              loading={insertLoading}
              size="sm"
              onClick={handleInsert}
              className="flex items-center gap-1.5"
            >
              <InsertArrowIcon className="w-4 h-4" />
              Use
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const EmailPreviewDialog: React.FC<EmailPreviewDialogProps> = props => {
  const { open } = props;
  // 延迟卸载，保留 Dialog 关闭动画
  const openV2 = useDialogVisible(open);
  if (!openV2) return null;
  return <PureEmailPreviewDialog {...props} />;
};
